import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { createDiagnostics } from '../utils/connectionDiagnostics';

interface ConnectionErrorAlertProps {
  supabaseUrl: string;
}

const ConnectionErrorAlert: React.FC<ConnectionErrorAlertProps> = ({ supabaseUrl }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [diagnosticsResult, setDiagnosticsResult] = useState<string>('');

  useEffect(() => {
    const handleConnectionFailed = (event: CustomEvent) => {
      console.log('🚨 Connection failed event received:', event.detail);
      setShowAlert(true);
      setErrorMessage(event.detail.error || 'No se pudo conectar con el servidor');

      runDiagnostics();
    };

    window.addEventListener('auth-connection-failed', handleConnectionFailed as EventListener);

    return () => {
      window.removeEventListener('auth-connection-failed', handleConnectionFailed as EventListener);
    };
  }, []);

  const runDiagnostics = async () => {
    const diagnostics = createDiagnostics(supabaseUrl);
    const result = await diagnostics.testConnection();
    const recommendation = diagnostics.getRecommendedAction(result);
    setDiagnosticsResult(recommendation);
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setDiagnosticsResult('Verificando conexión...');

    const diagnostics = createDiagnostics(supabaseUrl);
    const fullCheck = await diagnostics.runFullDiagnostics();

    if (fullCheck.overallStatus === 'healthy') {
      setDiagnosticsResult('¡Conexión restaurada! Recargando página...');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      const recommendation = diagnostics.getRecommendedAction(fullCheck.connection);
      setDiagnosticsResult(recommendation);
      setIsRetrying(false);
    }
  };

  const handleDismiss = () => {
    setShowAlert(false);
  };

  if (!showAlert) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0">
            <WifiOff className="h-6 w-6 text-red-600" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Error de Conexión
            </h3>
            <div className="mt-2 text-sm text-gray-600">
              <p className="mb-2">{errorMessage}</p>
              {diagnosticsResult && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start">
                    <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-blue-700">{diagnosticsResult}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Posibles soluciones:</h4>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Verifica tu conexión a internet</li>
            <li>Verifica que Supabase esté en línea</li>
            <li>Revisa la consola del navegador para más detalles</li>
            <li>Intenta recargar la página en unos minutos</li>
          </ul>
        </div>

        <div className="mt-6 flex space-x-3">
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Verificando...' : 'Reintentar'}
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cerrar
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center text-xs text-gray-500">
            <Wifi className="h-3 w-3 mr-1" />
            <span>Proyecto Supabase: {supabaseUrl.replace('https://', '').split('.')[0]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionErrorAlert;
